import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, Mic, Square, Send, Loader2, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import LinkConfirmCard from "@/components/LinkConfirmCard";

// Heuristic: does the user text look like a "Kada X, onda Y" sequence?
const isLinkPrompt = (text) => {
  const t = (text || "").toLowerCase();
  if (!t) return false;
  return /\bkada\b/.test(t) && /\b(onda|posle toga|nakon toga|zatim|pa|,\s*ja)\b/.test(t);
};

/**
 * Compact AI input used on the Dashboard bottom.
 * - `compact` (default): single-line input row with mic + send, expandable suggestions.
 * - Otherwise falls back to a larger card layout with textarea and hints.
 */
export default function AIAssistant({ onCreated, compact = false, collaborators = [] }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [linkParsed, setLinkParsed] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const submit = async () => {
    const p = prompt.trim();
    if (!p) return;
    setBusy(true);
    try {
      if (isLinkPrompt(p)) {
        const { data } = await api.post("/ai/parse-link", { text: p });
        setLinkParsed(data);
        setPrompt("");
        setBusy(false);
        return;
      }
      const { data } = await api.post("/ai/create-task", { prompt: p });
      toast.success(`Task kreiran za ${data.collaborator.name}`);
      setPrompt("");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];
      const mime = mimeCandidates.find((m) => !m || (window.MediaRecorder && MediaRecorder.isTypeSupported(m))) ?? "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/ai/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data.text || "").trim();
          if (!text) toast.error("Prazna transkripcija");
          else {
            setPrompt((p) => (p ? p + " " : "") + text);
            toast.success("Snimak transkribovan");
          }
        } catch (e) {
          toast.error(formatApiError(e));
        }
        setTranscribing(false);
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Nemam pristup mikrofonu");
    }
  };

  const stopRec = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    setRecording(false);
  };

  const suggestions = [
    "Luka do petka da mi dostavi kontakt za Tenzor, visok prioritet",
    "Marija: preksutra da posalje listu medija",
    "Mia: sutra da pripremi kolekciju za katalog, hitno",
  ];

  if (!compact) {
    return (
      <>
      {linkParsed && (
        <div className="mb-3">
          <LinkConfirmCard
            parsed={linkParsed}
            collaborators={collaborators}
            onDone={() => { setLinkParsed(null); onCreated?.(); }}
            onDiscard={() => setLinkParsed(null)}
          />
        </div>
      )}
      <div data-testid="ai-assistant" className="surface-card p-5 md:p-6 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-forest/5 blur-2xl pointer-events-none" />
        <div className="flex items-start justify-between gap-4 relative">
          <div>
            <div className="overline flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> AI asistent
            </div>
            <h3 className="font-heading text-lg md:text-xl font-semibold mt-1">
              Reci ili napiši šta treba da se uradi
            </h3>
            <p className="text-xs md:text-sm text-ink-muted mt-1">
              npr. „Luka do sutra da mi pošalje kontakt za Tenzor, visok prioritet“.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col md:flex-row gap-2 items-stretch">
          <div className="flex-1 relative">
            <textarea
              data-testid="ai-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={transcribing ? "Transkribujem..." : "Napiši prompt ili pritisni mikrofon da govoriš..."}
              className="w-full min-h-[80px] px-3 py-2.5 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-forest resize-y"
              disabled={busy || transcribing}
            />
          </div>
          <div className="flex md:flex-col gap-2">
            {recording ? (
              <button data-testid="ai-mic-stop" onClick={stopRec} className="btn-ghost !bg-terracotta !text-white !border-terracotta">
                <Square className="w-4 h-4" /> Zaustavi
              </button>
            ) : (
              <button data-testid="ai-mic-start" onClick={startRec} disabled={transcribing || busy} className="btn-ghost">
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />} Govori
              </button>
            )}
            <button data-testid="ai-submit-btn" onClick={submit} disabled={busy || !prompt.trim()} className="btn-primary">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Kreiraj
            </button>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Compact single-row layout (dashboard bottom)
  return (
    <div data-testid="ai-assistant-wrap">
    {linkParsed && (
      <div className="mb-3">
        <LinkConfirmCard
          parsed={linkParsed}
          collaborators={collaborators}
          onDone={() => { setLinkParsed(null); onCreated?.(); }}
          onDiscard={() => setLinkParsed(null)}
        />
      </div>
    )}
    <div data-testid="ai-assistant" className="surface-card p-3 relative">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-forest flex-shrink-0" />
        <input
          data-testid="ai-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={transcribing ? "Transkribujem..." : "Reci AI-u šta treba da se uradi..."}
          className="flex-1 bg-transparent px-1 py-2 text-sm focus:outline-none placeholder:text-ink-muted"
          disabled={busy || transcribing}
        />
        {recording ? (
          <button
            data-testid="ai-mic-stop"
            onClick={stopRec}
            className="p-2 rounded-md bg-terracotta text-white hover:opacity-90"
            title="Zaustavi"
          >
            <Square className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid="ai-mic-start"
            onClick={startRec}
            disabled={transcribing || busy}
            className="p-2 rounded-md text-ink-muted hover:text-forest hover:bg-surface-secondary transition-colors disabled:opacity-50"
            title="Govori"
          >
            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
        <button
          data-testid="ai-submit-btn"
          onClick={submit}
          disabled={busy || !prompt.trim()}
          className="p-2 rounded-md bg-forest text-white hover:opacity-90 disabled:opacity-40"
          title="Kreiraj"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
        <button
          data-testid="ai-hints-toggle"
          onClick={() => setShowHints((v) => !v)}
          className="p-2 rounded-md text-ink-muted hover:text-forest transition-colors"
          title="Primeri"
        >
          <ChevronUp className={`w-4 h-4 transition-transform ${showHints ? "" : "rotate-180"}`} />
        </button>
      </div>
      {showHints && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs" data-testid="ai-hints">
          {suggestions.map((s, i) => (
            <button
              key={i}
              data-testid={`ai-suggestion-${i}`}
              onClick={() => setPrompt(s)}
              className="text-ink-muted hover:text-forest bg-surface-secondary border border-line px-2 py-1 rounded transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}
